/**
 * Payload schemas for the runner protocol's busiest events that had none (harness to nine B5c, P1
 * item 3): billing, message persistence, the AGENT_READY handshake, interaction continues, the turn
 * reattach rail, drain and workspace placement, token and ability refresh, the box's activity and
 * log lines, and the permission and access frames. None of these is forwarded to the browser.
 *
 * Each schema is written from what its producers send today: every emit site was read and is named
 * on the schema. Presence follows the producers (a field some site leaves out is optional), types
 * follow their declarations. Where a hand-written type already described a payload it is now
 * DERIVED from the schema here (one author: relay-frames.ts, workspace-head.ts, import-types.ts,
 * events.ts AgentReadyPayload, credits.ts CreditPermissionResponsePayload).
 *
 * They describe; nothing here parses at runtime. The producer-conformance suites (apps/sandbox
 * wire-frames.contract.test.ts, apps/server server-direct-emits.contract.test.ts) drive the
 * producers and parse what they emit.
 */
import { z } from 'zod';
import { AccessLevelSchema, PermissionModeSchema } from './common.schema.js';
import { AccessConfigSchema } from './access-config.schema.js';
import { InteractionOutcomeSchema } from './interaction.schema.js';

// ============================================================================
// Billing (runner → server)
// ============================================================================

/**
 * agent:tool_billing: one per settled tool call. agent-sdk engine/tool-billing.ts toolBillingEvent
 * (a raw harness event from the round, continue and subagent loops and the CLI engines' bridge),
 * sent as is by apps/sandbox stream/harness-sink.ts. `toolUseId` is the server's idempotency key.
 */
export const ToolBillingPayloadSchema = z.object({
  taskId: z.string(),
  toolName: z.string(),
  toolUseId: z.string(),
  isSubagent: z.boolean(),
});

/**
 * agent:billing_tripwire: a turn whose billing failed its reconcile (agent-sdk
 * engine/billing-tripwire.ts BillingTripwirePayload, from native-harness.ts). Rare.
 */
export const BillingTripwirePayloadSchema = z.object({
  reason: z.enum(['reconcile_drift', 'zero_spend']),
  expected: z.number(),
  actual: z.number(),
  delta: z.number(),
  taskId: z.string(),
  /** The run's session id. */
  runId: z.string(),
  round: z.number(),
});

// ============================================================================
// Messages
// ============================================================================

/**
 * message:complete (runner → server): the person's message echo and the turn's assistant message,
 * apps/sandbox sdk/processor.ts emitUserMessageComplete / emitAssistantMessageComplete. Carries no
 * `taskId` (so it is not sequenced): `sessionId` is the task. Content blocks are passed through.
 */
export const MessageCompletePayloadSchema = z.object({
  sessionId: z.string(),
  agentSlug: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.array(z.object({ type: z.string() }).passthrough()),
  timestamp: z.number(),
  /** The person's message id (user echo only). */
  messageId: z.string().optional(),
  /** The journal point the turn ended at (assistant only, when known). */
  sdkMessageUuid: z.string().optional(),
  /** The turn ended paused on an ask (assistant only). */
  paused: z.boolean().optional(),
});

/** message:sync_request (web → server): re-pull one task's messages. Every web and extension site sends `{ taskId }`. */
export const MessageSyncRequestPayloadSchema = z.object({
  taskId: z.string(),
});

// ============================================================================
// The AGENT_READY handshake (runner → server)
// ============================================================================

/** The runner lease the box holds (types/runner-lease.ts RunnerLeasePayload). */
const RunnerLeaseSchema = z.object({ epoch: z.number(), holder: z.string(), environment: z.string() });

/**
 * agent:ready: apps/sandbox handlers/index.ts on attach (the dial client re-sends the same frame).
 * `loaded` is what the runner ACTUALLY has, every member optional: an identity the runner cannot
 * establish is absent, never guessed.
 */
export const AgentReadyPayloadSchema = z.object({
  protocolVersion: z.number(),
  capabilities: z.array(z.string()),
  /** @deprecated Use `loaded.release`. */
  loaded_release: z.string().optional(),
  loaded: z
    .object({
      /** Release id (bundle sha256) of the running process. */
      release: z.string().optional(),
      /** Image identity from a build-time stamp. */
      image: z.string().optional(),
      /** `.claude` content-manifest version actually materialized in the workspace. */
      volumeManifest: z.number().optional(),
      /** The engines this process can run (its engine table's own keys). */
      engines: z.array(z.string()).optional(),
      /** What it can do on those engines ('claude_code_slots'). */
      engineFeatures: z.array(z.string()).optional(),
    })
    .optional(),
  /** The lease this runner holds (runner-lease-v1). */
  lease: RunnerLeaseSchema.optional(),
  /** The runner proved it needs no lease. */
  leaseless: z.boolean().optional(),
  /** `runner:<8hex>`, when the lease or reattach rail is on. */
  runnerHolder: z.string().optional(),
});

// ============================================================================
// Interactions
// ============================================================================

/** The interaction kinds the server's rows and the continue path carry (agent-sdk InteractionKind), wider than InteractionKindSchema's request set. */
const ContinueInteractionKindSchema = z.enum([
  'ask_question',
  'composio_auth',
  'tool_permission',
  'credit_approval',
  'exit_plan',
  'custom',
  'hands_approval',
  'plan_limit',
]);

/**
 * interaction:continue (server → runner): a resolved ask, resumed. apps/server
 * agent-push.service.ts, from interaction-continue.service.ts. The company's context rides as
 * `platform` (TaskPlatformContext; its org id pinned, the rest passed through).
 */
export const ContinueInteractionPayloadSchema = z.object({
  taskId: z.string(),
  interactionId: z.string(),
  toolUseId: z.string().nullable(),
  kind: ContinueInteractionKindSchema,
  outcome: InteractionOutcomeSchema,
  subagentId: z.string().optional(),
  elapsedMs: z.number().optional(),
  /** The engine the task is locked to (HarnessId). */
  runtime: z.string(),
  finalAttempt: z.boolean().optional(),
  engineSlotId: z.string().optional(),
  platform: z.object({ orgId: z.string() }).passthrough().optional(),
  /**
   * Agent wallet (spec 2026-10-15 §1): the allowance the continued turn opens with (resolveTurnCaps; the turn that
   * carries a purchase's result gets it with purchaseContinue). Absent when no cap applies or the read failed: the turn
   * reads the last mint, as before.
   */
  maxBudgetUsd: z.number().nonnegative().optional(),
  maxBudgetSource: z.string().optional(),
  /**
   * The person's capability manifest, minted for this continue as for every USER_MESSAGE (the same list of ids), so a
   * runner that restarted since their last message keeps browser control. Absent when the mint failed, and from an
   * older server: the box keeps the manifest it has.
   */
  capabilities: z.array(z.string()).optional(),
});

/** continue:received (runner → server): the box took a continue. apps/sandbox handlers/continue-interaction.ts. */
export const ContinueReceivedPayloadSchema = z.object({
  taskId: z.string(),
  interactionId: z.string(),
});

// ============================================================================
// The turn reattach rail (spec 2026-08-31 §4; types/relay-frames.ts derives from these)
// ============================================================================

/** relay:resume (server → runner): relay/reattach-handshake.ts, the last seq it holds per task. */
export const RelayResumePayloadSchema = z.object({
  tasks: z.array(z.object({ taskId: z.string(), lastSeq: z.number() })),
});

/** relay:resumed (runner → server): handlers/relay-resume.ts. `fromSeq > toSeq` = nothing replayed. */
export const RelayResumedPayloadSchema = z.object({
  tasks: z.array(z.object({ taskId: z.string(), fromSeq: z.number(), toSeq: z.number(), open: z.boolean() })),
});

/** relay:ack (server → runner): reattach-handshake.ts ackAndRelease; the seq the server holds through. */
export const RelayAckPayloadSchema = z.object({
  taskId: z.string(),
  seq: z.number(),
});

/** relay:gap (runner → server): handlers/relay-resume.ts, frames the outbox no longer holds. */
export const RelayGapPayloadSchema = z.object({
  taskId: z.string(),
  from: z.number(),
  to: z.number(),
});

// ============================================================================
// Drain and workspace placement
// ============================================================================

/**
 * runner:drain (server → runner): sandbox-relay.service.ts signalRunnerDrain (rotation, account block,
 * converge), and release/wake-relaunch.ts WAKE_RELAUNCH_DRAIN (the one-actor wake): a READY or a turn
 * boundary waiting on the cloud runner's own relaunch, `{ reason: 'wake-relaunch', exitCode: 76, urgent: true }`,
 * sent only to a runner that advertised WAKE_RELAUNCH_CAPABILITY.
 * Without that pair it is the drain it always was (exit 75 at conversational idle).
 */
export const RunnerDrainPayloadSchema = z.object({
  reason: z.enum(['rotation', 'wake-relaunch']),
  exitCode: z.literal(76).optional(),
  urgent: z.literal(true).optional(),
});

/** runner:draining (runner → server): apps/sandbox index.ts notifyDraining, from the drain controller. */
export const RunnerDrainingPayloadSchema = z.object({
  reason: z.literal('self-update'),
  timestamp: z.number(),
});

/** runner:move_doorbell (server → runner): sandbox-relay.service.ts signalMoveDoorbell (today 'local'); apps/sandbox handlers/index.ts beats now when lease-less. */
export const MoveDoorbellPayloadSchema = z.object({
  target: z.string(),
});

/** runner:idle_release (server → runner): relay/dial-out-idle.ts releaseIdleDialOut; apps/sandbox handlers/index.ts (runner/idle-release.ts). It carries nothing. */
export const RunnerIdleReleasePayloadSchema = z.object({}).strict();

/**
 * How long each step of an acquire's workspace materialize took (apps/sandbox workspace-head
 * acquire.ts and materialize.ts), carried on the materialized ack and on a refusal so a slow move
 * can be read from the server's log: the runner's own log can die with its box (boat drill 3).
 * Every field is optional: a step that did not run is absent, and a runner from before these
 * timings sends none at all.
 */
export const MaterializeTimingsSchema = z.object({
  /** Reading the newest recorded head from the server. */
  latestMs: z.number().optional(),
  /** Reading this box's own workspace head (its marker, or a full hash). */
  localHeadMs: z.number().optional(),
  /** Downloading the artifact, every try together. */
  downloadMs: z.number().optional(),
  /** How many tries the download took. */
  tries: z.number().optional(),
  /** Checking the artifact's hash and unpacking it beside the workspace. */
  extractMs: z.number().optional(),
  /** Moving the unpacked tree into the live workspace, and clearing the unpacked copy. */
  swapMs: z.number().optional(),
  /** A manifest head (box hosts, Plan 4): how many blobs the move downloaded, and their bytes. */
  blobs: z.number().optional(),
  downloadBytes: z.number().optional(),
});

/**
 * runner:workspace_materialized (runner → server): handlers/index.ts once per connect and lease
 * reacquire (types/workspace-head.ts WorkspaceMaterializedEvent derives from this).
 */
export const WorkspaceMaterializedEventSchema = z.object({
  epoch: z.number(),
  /** The environment the runner just proved materialized ('blaxel' | 'local'). Absent from a pre-P2 runner. */
  environment: z.string().optional(),
  /** How long each step of the materialize took. Absent from a runner from before them. */
  timings: MaterializeTimingsSchema.optional(),
});

/**
 * runner:workspace_head_mismatch (runner → server): handlers/index.ts, from
 * services/workspace-head/acquire.ts, when a move is refused (types/workspace-head.ts
 * WorkspaceHeadMismatchEvent derives from this).
 */
export const WorkspaceHeadMismatchEventSchema = z.object({
  userId: z.string(),
  epoch: z.number(),
  /** The head the runner was required to materialize ('unknown' when it could not be read). */
  expectedHead: z.string(),
  /** The head it actually got, when the artifact extracted but failed verification. */
  actualHead: z.string().optional(),
  artifactRef: z.string().optional(),
  /** 'fetch_failed' (could not download) | 'hash_mismatch' (downloaded, did not verify). */
  reason: z.enum(['fetch_failed', 'hash_mismatch']),
  /** The refusing runner (the move's target). */
  environment: z.string().optional(),
  /** The environment the recorded head came from (the move's source). */
  sourceEnvironment: z.string().optional(),
  /** How long each step ran before the refusal. Absent from a runner from before them. */
  timings: MaterializeTimingsSchema.optional(),
});

// ============================================================================
// Token and ability refresh (server → runner)
// ============================================================================

/** token:refresh: sandbox-relay.service.ts pushTokenRefresh, on attach. */
export const TokenRefreshPayloadSchema = z.object({
  token: z.string(),
});

/**
 * skill:reload (EVENTS.ABILITY_RELOAD): sandbox-relay.service.ts requestAbilityReload and the queued
 * flush at AGENT_READY. Often `{}` (items/import-types.ts AbilityReloadPayload derives from this).
 */
export const AbilityReloadPayloadSchema = z.object({
  /** 'sha256:<hex>' declared abilities head, when the caller wants it echoed. */
  head: z.string().optional(),
});

// ============================================================================
// The box's activity and log lines (runner → server)
// ============================================================================

/**
 * sandbox:idle and sandbox:active: apps/sandbox state/activity-manager.ts. `taskId` and `sessionId`
 * both carry the TRIGGER ('task:<id>', 'ws-connected', 'touch', ...), not necessarily a task id.
 */
export const SandboxLifecyclePayloadSchema = z.object({
  taskId: z.string(),
  sessionId: z.string(),
  timestamp: z.number(),
  summary: z.object({
    isQueryRunning: z.boolean(),
    runningTools: z.number(),
    activeSubagents: z.number(),
    activeTasks: z.number(),
  }),
});

/** sandbox:state_change: activity-manager.ts on every activity start or stop and on connect. `taskId` is '' when no task triggered it. */
export const SandboxStateChangePayloadSchema = z.object({
  status: z.enum(['active', 'browsing', 'idle']),
  activeTaskCount: z.number(),
  activityReasons: z.array(z.string()),
  taskId: z.string(),
  timestamp: z.number(),
  phase: z.literal('ready'),
});

/** sandbox:log: one shipped log line (apps/sandbox utils/log-ship.ts ShippedLog). The busiest frame of all. */
export const SandboxLogPayloadSchema = z.object({
  taskId: z.string(),
  traceId: z.string().optional(),
  /** The logger's level ('debug' | 'info' | 'warn' | 'error'; shipLog takes a string). */
  level: z.string(),
  context: z.string().nullable(),
  message: z.string(),
});

// ============================================================================
// Permission and access (web → server, server → runner, runner → server)
// ============================================================================

/**
 * permission:set_mode: the extension's toggle (web → server) and the server's per-turn and relayed
 * set (server → runner: session-policy.ts, interaction-handlers.ts). The runner drops a mode it
 * does not know.
 */
export const SetPermissionModePayloadSchema = z.object({
  taskId: z.string(),
  mode: PermissionModeSchema.optional(),
});

/**
 * SET_ACCESS_LEVEL: the picker (web → server; null or absent clears the task's override) and the
 * server's set (server → runner: never null).
 */
export const SetAccessLevelPayloadSchema = z.object({
  taskId: z.string(),
  level: AccessLevelSchema.nullable().optional(),
});

/** permission:rules_list_request (server → runner): handlers/permission-rules-routes.handler.ts. */
export const PermissionRulesListRequestPayloadSchema = z.object({
  taskId: z.string(),
  requestId: z.string(),
});

/** permission:rules_list_result (runner → server): apps/sandbox handlers/permission-rules.ts. */
export const PermissionRulesListResultPayloadSchema = z.object({
  requestId: z.string(),
  taskId: z.string(),
  rules: z.array(z.object({ rule: z.string(), source: z.enum(['task', 'always']) })),
});

/** permission:rule_remove_request (server → runner): handlers/permission-rules-routes.handler.ts. */
export const PermissionRuleRemoveRequestPayloadSchema = z.object({
  taskId: z.string(),
  rule: z.string(),
  source: z.enum(['task', 'always']),
  requestId: z.string(),
});

/** permission:rule_remove_result (runner → server): apps/sandbox handlers/permission-rules.ts. */
export const PermissionRuleRemoveResultPayloadSchema = z.object({
  requestId: z.string(),
  taskId: z.string(),
  ok: z.boolean(),
});

/**
 * permission:rules_set (server → runner): session-policy.ts each turn, the routes on a revoke. An
 * empty list revokes everything. A company's rules are its stored objects, passed through.
 */
export const PermissionRulesSetPayloadSchema = z.object({
  taskId: z.string(),
  rules: z.array(z.object({ rule: z.string(), behavior: z.enum(['allow', 'ask', 'deny']) }).passthrough()),
});

/**
 * permission:config_set (server → runner): the custom access config, or null to clear it
 * (session-policy.ts, interaction-handlers.ts, composer/dispatch.ts, routine-executor.service.ts).
 * The engine's AccessConfig can carry keys the write-path schema does not (`bashChats`): passed
 * through.
 */
export const PermissionConfigSetPayloadSchema = z.object({
  taskId: z.string(),
  config: AccessConfigSchema.passthrough().nullable(),
});

/**
 * runner:record_head (server → runner): apps/server services/hosts/head-save.ts requestHeadSave, the
 * move job's "save now" before a switch (box hosts, Plan 4, 4-R6).
 */
export const RunnerRecordHeadRequestSchema = z.object({ requestId: z.string().min(1) });

/**
 * runner:record_head_result (runner → server): apps/sandbox handlers/head-save.ts, the one answer per
 * request. `outcome` names how the save ended (services/workspace-head/record.ts RecordOutcome).
 */
export const RunnerRecordHeadResultSchema = z.object({
  requestId: z.string().min(1),
  outcome: z.enum(['recorded', 'restamped', 'unchanged', 'continues', 'failed', 'off']),
  head: z.string().regex(/^[0-9a-f]{64}$/).optional(),
  epoch: z.number().int().positive().optional(),
  detail: z.string().max(500).optional(),
});
export type RunnerRecordHeadRequest = z.infer<typeof RunnerRecordHeadRequestSchema>;
export type RunnerRecordHeadResult = z.infer<typeof RunnerRecordHeadResultSchema>;

// ============================================================================
// Credits (web → server, server → web)
// ============================================================================

/**
 * credit:permission_response (web → server): stores/creditStore.ts, from an approval card
 * (types/credits.ts CreditPermissionResponsePayload derives from this).
 */
export const CreditPermissionResponsePayloadSchema = z.object({
  requestId: z.string(),
  approved: z.boolean(),
});

/**
 * credit:blocked (runner → server): apps/sandbox hooks/credit-hook.ts, when a tool is refused for
 * exhausted credits. That hook is wired only through createHooksConfig, which no live path calls;
 * the shape is its emit's.
 */
export const CreditBlockedPayloadSchema = z.object({
  toolName: z.string(),
  balance: z.number(),
  sessionId: z.string(),
});

/** credit:summary_stale (server → web): credit-stream.service.ts when a summary could not be built. */
export const CreditSummaryStalePayloadSchema = z.object({
  userId: z.string(),
});
